import { executeApi, type ExecuteApiProps } from '../Utils';

export interface MiddlewhereFile {
	name: string;
	size: number;
	content?: string;
	dateModified: string;
	isDirectory?: boolean;
}

type UploadFilesApiProps = {
	path: string;
	files: File[];
} & Pick<ExecuteApiProps, 'dispatch' | 'successHandler' | 'errorHandler'>;

type DeleteFileApiProps = {
	path: string;
} & Pick<ExecuteApiProps, 'dispatch' | 'successHandler' | 'errorHandler'>;

type LogoApiProps = Pick<ExecuteApiProps, 'url' | 'dispatch' | 'successHandler' | 'errorHandler'>;

export async function uploadFiles({ path, files, dispatch, successHandler, errorHandler }: UploadFilesApiProps) {
	const body = new FormData();
	Array.from(files).map(file => body.append(file.name.replace(/\.[^/.]+$/, ''), file));

	executeApi.post({
		url: `/api/filexchange/upload/file?path=${path}`,
		body,
		config: { headers: { 'Content-Type': 'multipart/form-data' } },
		dispatch,
		successHandler,
		errorHandler
	});
}

export async function deleteFile({ path, dispatch, successHandler, errorHandler }: DeleteFileApiProps) {
	executeApi.delete({
		url: `/api/filexchange/file?path=${path}`,
		dispatch,
		successHandler,
		errorHandler
	});
}

export async function grabLogoImage({ url, dispatch, successHandler, errorHandler }: LogoApiProps) {
	executeApi.get({
		url: `/api/filexchange/logo/grab?url=${encodeURIComponent(url)}`,
		dispatch,
		successHandler,
		errorHandler
	});
}

export function getFileExtension(fileName: string) {
	return fileName.split('.').slice(-1)[0].toLowerCase();
}

export function getFileMime(fileName: string) {
	const extension = getFileExtension(fileName);

	switch (extension) {
		case 'txt':
			return 'text/plain';
		case 'csv':
			return 'text/csv';
		case 'bmp':
		case 'gif':
		case 'ico':
		case 'jpg':
		case 'jpeg':
		case 'png':
			return `image/${extension}`;
		case 'svg':
			return 'image/svg+xml';
		case 'pdf':
			return 'application/pdf';
		case 'doc':
		case 'docx':
			return 'application/msword';
		case 'xls':
		case 'xlsx':
		case 'xlsm':
			return 'application/vnd.ms-excel';
		case 'msg':
			return 'application/vnd.ms-outlook';
		case 'eml':
			return 'application/octet-stream';
		case 'mkv':
		case 'mov':
		case 'mp4':
		case 'webm':
		case 'wmv':
			return `video/${extension}`;
		case 'mp3':
			return `audio/${extension}`;
		case 'rar':
			return 'application/vnd.rar';
		case 'zip':
			return 'application/zip';
		case '7z':
			return 'application/x-7z-compressed';
		default: {
			console.error(`${extension} isn't implemented`);
			return undefined;
		}
	}
}

export function getFileDataType(file: MiddlewhereFile) {
	const extension = getFileExtension(file.name);
	return getFileMime(extension);
}

export function getFileIcon(file: MiddlewhereFile) {
	const extension = getFileExtension(file.name);

	if (file.isDirectory) return 'folder-outline';
	switch (extension) {
		case 'txt':
			return 'file-document-outline';
		case 'csv':
			return 'file-delimited-outline';
		case 'bmp':
		case 'gif':
		case 'ico':
		case 'jpg':
		case 'jpeg':
		case 'png':
			return 'file-image-outline';
		case 'svg':
			return 'svg';
		case 'pdf':
			return 'file-pdf-outline';
		case 'doc':
		case 'docx':
			return 'file-word-outline';
		case 'xls':
		case 'xlsx':
		case 'xlsm':
			return 'file-excel-outline';
		case 'msg':
		case 'eml':
			return 'mail-outline';
		case 'mkv':
		case 'mov':
		case 'mp4':
		case 'webm':
		case 'wmv':
			return 'file-video-outline';
		case 'mp3':
			return 'file-music-outline';
		case 'rar':
		case 'zip':
		case '7z':
			return 'folder-zip-outline';
		default:
			return 'file-outline';
	}
}

export function getFileSize(file: MiddlewhereFile) {
	if (file.isDirectory) return undefined;

	return file.size <= 1000
		? `${file.size} byte`
		: file.size <= 1000000
			? `${(file.size / 1000).toFixed(2)} KB`
			: `${(file.size / 1000000).toFixed(2)} MB`;
}

export default {
	deleteFile,
	getFileDataType,
	getFileExtension,
	getFileIcon,
	getFileMime,
	getFileSize,
	grabLogoImage,
	uploadFiles
};
